import {Divider, TimeReverseIcon, Typography} from '@genestack/ui';
import classNames from 'classnames';
import React from 'react';

import {CustomSuggestionItem} from './custom-suggestion-item';
import styles from './sidebar-seach-field.module.css';
import {
    DOC_NAME_SEARCH_PREFIX,
    CATEGORY_SEARCH_PREFIX,
    useSuggestionsProps
} from './hooks/use-suggestions-props';
import {
    AutocompletionProps,
    SuggestionItem
} from '../../../../components/autocompletion/suggestion-item';
import {SearchSuggestion} from './interface';
import {useSidebarSearchProps} from './hooks/use-sidebar-search-props';
import {Autocompletion} from '../../../../components/autocompletion/autocompletion';

interface Props {
    disabled: boolean;
    busy: boolean;
    searchSidebarProps: ReturnType<typeof useSidebarSearchProps>;
    onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
    suggestionsProps: ReturnType<typeof useSuggestionsProps>;
}

function getAutocompletionPlaceholder(type: string) {
    if (!type) {
        return 'Search';
    }

    if (type === DOC_NAME_SEARCH_PREFIX) {
        return 'Search by document name';
    }

    if (type === CATEGORY_SEARCH_PREFIX) {
        return 'Search by category';
    }

    return `Search by ${type}`;
}

function getOpenStatusBySearchType(inputValueType: string, isOpen: boolean) {
    return isOpen && inputValueType !== DOC_NAME_SEARCH_PREFIX;
}

function getEmptyListText(searchPrefix: string) {
    return searchPrefix ? 'No results available' : '';
}

export function SidebarSearchField(props: Props) {
    const {disabled, onBlur, searchSidebarProps, suggestionsProps, busy} = props;
    const {isOpen, handleOpenSuggestions, handleCloseSuggestions} = searchSidebarProps;

    const onKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const target = e.currentTarget;

            if (
                searchSidebarProps.inputPrefix &&
                e.key === 'Backspace' &&
                target.selectionStart === 0 &&
                target.selectionEnd === 0
            ) {
                e.preventDefault();
                searchSidebarProps.onPrefixDeleted();
            }
        },

        [searchSidebarProps]
    );

    const handleAutocompletionFocus = React.useCallback(() => {
        // invalidating cache on every focus so that user
        // doesn't miss node types that might have been added recently
        suggestionsProps.invalidateNodeTypesQuery();

        if (!searchSidebarProps.inputPrefix && !searchSidebarProps.inputValue) {
            handleOpenSuggestions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchSidebarProps.inputPrefix]);

    const hasDocSuggestion = !!suggestionsProps.docSuggestion;
    const hasNodeTypesSuggestions = suggestionsProps.prefixSuggestions.length > 0;
    const hasNodeOrCategorySuggestions = suggestionsProps.nodeAndCategorySuggestions.length > 0;

    const clearButtonTooltip = (function () {
        if (searchSidebarProps.inputPrefix) {
            return 'Remove operator';
        }

        if (searchSidebarProps.inputValue) {
            return 'Clear search bar';
        }

        return undefined;
    })();

    return (
        <Autocompletion<SearchSuggestion>
            autoFocus
            inputAdditionalClassname={classNames(styles.root, {
                [styles.green]: Boolean(searchSidebarProps.inputPrefix)
            })}
            disabled={disabled}
            busy={busy}
            placeholder={getAutocompletionPlaceholder(searchSidebarProps.inputPrefix)}
            isOpen={getOpenStatusBySearchType(searchSidebarProps.inputPrefix, isOpen)}
            onClose={handleCloseSuggestions}
            value={searchSidebarProps.inputValue}
            onValueChange={searchSidebarProps.onInputValueChange}
            onFocus={handleAutocompletionFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onClearButtonClick={searchSidebarProps.onClearClick}
            suggestions={
                [
                    ...suggestionsProps.historySuggestions,
                    ...(suggestionsProps.docSuggestion ? [suggestionsProps.docSuggestion] : []),
                    ...suggestionsProps.prefixSuggestions,
                    ...suggestionsProps.nodeAndCategorySuggestions,
                    ...suggestionsProps.metadataSuggestions
                ] as SearchSuggestion[]
            }
            prepend={
                searchSidebarProps.inputPrefix && (
                    <span className={styles.prepend}>{searchSidebarProps.inputPrefix}: </span>
                )
            }
            suggestionHandler={searchSidebarProps.handleSuggestionSelected}
            emptyListText={getEmptyListText(searchSidebarProps.inputPrefix)}
            clearable={!!clearButtonTooltip}
            clearButtonTooltip={clearButtonTooltip}
            // FIXME fix behaviour in UI-library
            shortcutHandlers={{
                '\u00A0': handleOpenSuggestions
            }}
        >
            {(autocompleteProps: AutocompletionProps) => (
                <React.Fragment>
                    {!searchSidebarProps.inputValue && !searchSidebarProps.inputPrefix && (
                        <Typography
                            className={classNames(styles.caption, styles.rightCaption)}
                            intent="quiet"
                            variant="caption"
                        >
                            Type "
                            <Typography as="span" variant="caption">
                                :
                            </Typography>
                            " for search operators
                        </Typography>
                    )}

                    {suggestionsProps.historySuggestions.length > 0 &&
                        suggestionsProps.historySuggestions.map((historySuggestion, i) => (
                            <SuggestionItem
                                key={i}
                                value={historySuggestion}
                                prepend={<TimeReverseIcon />}
                                autocompletionProps={autocompleteProps}
                            >
                                <CustomSuggestionItem
                                    suggestion={historySuggestion}
                                    searchInput={searchSidebarProps.inputValue}
                                />
                            </SuggestionItem>
                        ))}

                    {suggestionsProps.docSuggestion && (
                        <SuggestionItem
                            value={suggestionsProps.docSuggestion}
                            autocompletionProps={autocompleteProps}
                        >
                            <CustomSuggestionItem
                                suggestion={suggestionsProps.docSuggestion}
                                searchInput={searchSidebarProps.inputValue}
                            />
                        </SuggestionItem>
                    )}

                    {hasNodeTypesSuggestions && (
                        <>
                            <Typography className={styles.caption} intent="quiet" variant="caption">
                                Operators to narrow search results by type:
                            </Typography>

                            {suggestionsProps.prefixSuggestions.map((suggestion, i) => (
                                <SuggestionItem
                                    key={i}
                                    value={suggestion}
                                    autocompletionProps={autocompleteProps}
                                >
                                    <CustomSuggestionItem
                                        suggestion={suggestion}
                                        searchInput={searchSidebarProps.inputValue}
                                    />
                                </SuggestionItem>
                            ))}
                        </>
                    )}

                    {suggestionsProps.metadataSuggestions.length > 0 && (
                        <React.Fragment>
                            {(hasDocSuggestion ||
                                hasNodeTypesSuggestions ||
                                hasNodeOrCategorySuggestions) && <Divider />}

                            <Typography className={styles.caption} intent="quiet" variant="caption">
                                Search by metadata and tags:
                            </Typography>

                            {suggestionsProps.metadataSuggestions.map((suggestion, i) => (
                                <SuggestionItem
                                    key={i}
                                    value={suggestion}
                                    autocompletionProps={autocompleteProps}
                                >
                                    <CustomSuggestionItem
                                        suggestion={suggestion}
                                        searchInput={searchSidebarProps.inputValue}
                                    />
                                </SuggestionItem>
                            ))}
                        </React.Fragment>
                    )}

                    {hasNodeOrCategorySuggestions && (
                        <React.Fragment>
                            {(hasDocSuggestion || hasNodeTypesSuggestions) && <Divider />}

                            <Typography className={styles.caption} intent="quiet" variant="caption">
                                Search by entity:
                            </Typography>

                            {suggestionsProps.nodeAndCategorySuggestions.map((suggestion, i) => (
                                <SuggestionItem
                                    key={i}
                                    value={suggestion}
                                    autocompletionProps={autocompleteProps}
                                >
                                    <CustomSuggestionItem
                                        suggestion={suggestion}
                                        searchInput={searchSidebarProps.inputValue}
                                    />
                                </SuggestionItem>
                            ))}
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </Autocompletion>
    );
}
